import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import customClaimsService from '../services/customClaimsService';

/**
 * Hook personnalisé pour gérer les Custom Claims Firebase
 * @returns {Object} État et fonctions des Custom Claims
 */
export const useCustomClaims = () => {
  const { currentUser: user, loading, userProfile } = useAuth();
  const [claims, setClaims] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsError, setClaimsError] = useState(null);

  // Mettre à jour les claims quand ils changent
  useEffect(() => {
    if (!user) {
      customClaimsService.setFallbackRole(null);
      setClaims(null);
      setClaimsLoading(false);
      setClaimsError(null);
      return;
    }

    setClaimsLoading(true);
    setClaimsError(null);
    customClaimsService.setFallbackRole(userProfile?.role || null);

    if (userProfile?.role) {
      setClaims((previousClaims) => previousClaims || { role: userProfile.role });
    }

    // Écouter les changements de claims
    const unsubscribe = customClaimsService.addClaimsListener((newClaims) => {
      if (newClaims) {
        setClaims(newClaims);
      } else if (userProfile?.role) {
        setClaims({ role: userProfile.role });
      } else {
        setClaims(null);
      }
      setClaimsLoading(false);
    });

    // Nettoyer lors du démontage
    return () => {
      unsubscribe();
    };
  }, [user, userProfile?.role]);

  // Fonctions utilitaires
  const isAdmin = useCallback(() => {
    return customClaimsService.isAdmin();
  }, []);

  const isReseller = useCallback(() => {
    return customClaimsService.isReseller();
  }, []);

  const hasRole = useCallback((role) => {
    return customClaimsService.hasRole(role);
  }, []);

  const hasAnyRole = useCallback((roles) => {
    return customClaimsService.hasAnyRole(roles);
  }, []);

  const getCurrentUserRole = useCallback(() => {
    return customClaimsService.getCurrentUserRole();
  }, []);

  // Fonctions d'administration
  const setUserRole = useCallback(async (targetUserId, role) => {
    try {
      setClaimsError(null);
      const result = await customClaimsService.setUserRole(targetUserId, role);
      return result;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    }
  }, []);

  const getUserRole = useCallback(async (userId = null) => {
    try {
      setClaimsError(null);
      const result = await customClaimsService.getUserRole(userId);
      return result;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    }
  }, []);

  const listUsersWithRoles = useCallback(async (limit = 100, pageToken = null) => {
    try {
      setClaimsError(null);
      const result = await customClaimsService.listUsersWithRoles(limit, pageToken);
      return result;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    }
  }, []);

  const revokeUserTokens = useCallback(async (userId) => {
    try {
      setClaimsError(null);
      const result = await customClaimsService.revokeUserTokens(userId);
      return result;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    }
  }, []);

  const saveOperatorTelegramConfig = useCallback(async (targetUserId, config) => {
    try {
      setClaimsError(null);
      const result = await customClaimsService.saveOperatorTelegramConfig(targetUserId, config);
      return result;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    }
  }, []);

  const deleteOperatorTelegramConfig = useCallback(async (targetUserId) => {
    try {
      setClaimsError(null);
      const result = await customClaimsService.deleteOperatorTelegramConfig(targetUserId);
      return result;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    }
  }, []);

  const refreshClaims = useCallback(async () => {
    try {
      setClaimsLoading(true);
      setClaimsError(null);
      const newClaims = await customClaimsService.getCurrentUserClaims(true);
      setClaims(newClaims);
      return newClaims;
    } catch (error) {
      setClaimsError(error.message);
      throw error;
    } finally {
      setClaimsLoading(false);
    }
  }, []);

  return {
    // État
    user,
    loading: loading || claimsLoading,
    error: claimsError,
    claims,
    claimsLoading,
    claimsError,
    
    // Vérifications de rôle
    isAdmin,
    isReseller,
    hasRole,
    hasAnyRole,
    getCurrentUserRole,
    
    // Fonctions d'administration
    setUserRole,
    getUserRole,
    listUsersWithRoles,
    revokeUserTokens,
    saveOperatorTelegramConfig,
    deleteOperatorTelegramConfig,
    
    // Utilitaires
    refreshClaims
  };
};

/**
 * Hook pour vérifier si l'utilisateur a un rôle spécifique
 * @param {string} requiredRole - Rôle requis
 * @returns {Object} État de la vérification
 */
export const useRequireRole = (requiredRole) => {
  const { user, loading, claims, claimsLoading, hasRole } = useCustomClaims();
  
  const hasRequiredRole = hasRole(requiredRole);
  const isAuthorized = user && !loading && !claimsLoading && hasRequiredRole;
  const isUnauthorized = user && !loading && !claimsLoading && !hasRequiredRole;
  
  return {
    user,
    loading: loading || claimsLoading,
    claims,
    hasRequiredRole,
    isAuthorized,
    isUnauthorized,
    currentRole: claims?.role || null
  };
};

/**
 * Hook pour vérifier si l'utilisateur a au moins un des rôles spécifiés
 * @param {string[]} requiredRoles - Liste des rôles acceptés
 * @returns {Object} État de la vérification
 */
export const useRequireAnyRole = (requiredRoles) => {
  const { user, loading, claims, claimsLoading, hasAnyRole } = useCustomClaims();
  
  const hasRequiredRole = hasAnyRole(requiredRoles);
  const isAuthorized = user && !loading && !claimsLoading && hasRequiredRole;
  const isUnauthorized = user && !loading && !claimsLoading && !hasRequiredRole;
  
  return {
    user,
    loading: loading || claimsLoading,
    claims,
    hasRequiredRole,
    isAuthorized,
    isUnauthorized,
    currentRole: claims?.role || null,
    requiredRoles
  };
};

/**
 * Hook spécialisé pour les administrateurs
 * @returns {Object} État spécifique aux admins
 */
export const useAdminRole = () => {
  return useRequireRole('admin');
};

/**
 * Hook spécialisé pour les revendeurs
 * @returns {Object} État spécifique aux revendeurs
 */
export const useResellerRole = () => {
  return useRequireRole('reseller');
};

/**
 * Hook pour les fonctionnalités admin/reseller
 * @returns {Object} État pour admin ou reseller
 */
export const useAdminOrResellerRole = () => {
  return useRequireAnyRole(['admin', 'reseller']);
};

export default useCustomClaims;

// 📝 EXEMPLES D'UTILISATION :
// 
// // 1. Utilisation basique
// function MyComponent() {
//   const { isAdmin, isReseller, loading, currentRole } = useCustomClaims();
//   
//   if (loading) return <div>Chargement...</div>;
//   
//   return (
//     <div>
//       <p>Rôle actuel: {currentRole || 'Aucun'}</p>
//       {isAdmin() && <AdminPanel />}
//       {isReseller() && <ResellerPanel />}
//     </div>
//   );
// }
// 
// // 2. Protection de route admin
// function AdminRoute({ children }) {
//   const { isAuthorized, isUnauthorized, loading } = useAdminRole();
//   
//   if (loading) return <div>Vérification des permissions...</div>;
//   if (isUnauthorized) return <div>Accès refusé</div>;
//   if (isAuthorized) return children;
//   
//   return <div>Veuillez vous connecter</div>;
// }
// 
// // 3. Gestion des rôles
// function UserManagement() {
//   const { setUserRole, listUsersWithRoles } = useCustomClaims();
//   
//   const handlePromoteUser = async (userId) => {
//     try {
//       await setUserRole(userId, 'admin');
//       alert('Utilisateur promu admin');
//     } catch (error) {
//       alert('Erreur: ' + error.message);
//     }
//   };
//   
//   // ...
// }
// 
// // 4. Protection flexible
// function FlexibleComponent() {
//   const { isAuthorized, currentRole } = useRequireAnyRole(['admin', 'reseller']);
//   
//   if (!isAuthorized) {
//     return <div>Accès réservé aux admins et revendeurs</div>;
//   }
//   
//   return (
//     <div>
//       <h1>Panneau {currentRole}</h1>
//       {/* Contenu autorisé */}
//     </div>
//   );
// }
